import React, { useMemo } from 'react';
import { HOME, UI } from '../constants/colors';

// ── Camembert concentrique statique (même rendu visuel que le ConcentricPie
//    du Dashboard, sans interactions). Utilisé sur les cartes coach et la
//    page de détail d'une sportive. ──

const EMPTY_SLICE = [{ value: 1, fill: HOME.emptySlice }];

function toSlices(slices) {
  if (!slices || slices.every(s => s.pct === 0)) return EMPTY_SLICE;
  return slices.map(s => ({ value: s.pct, fill: s.color, label: s.label }));
}

function arcPath(cx, cy, r1, r2, startDeg, endDeg) {
  if (endDeg - startDeg >= 360) {
    const mid = startDeg + 180;
    return arcPath(cx, cy, r1, r2, startDeg, mid) + ' ' + arcPath(cx, cy, r1, r2, mid, endDeg - 0.001);
  }
  const toRad = d => ((d - 90) * Math.PI) / 180;
  const s = toRad(startDeg), e = toRad(endDeg);
  const x1 = cx + r2 * Math.cos(s), y1 = cy + r2 * Math.sin(s);
  const x2 = cx + r2 * Math.cos(e), y2 = cy + r2 * Math.sin(e);
  const x3 = cx + r1 * Math.cos(e), y3 = cy + r1 * Math.sin(e);
  const x4 = cx + r1 * Math.cos(s), y4 = cy + r1 * Math.sin(s);
  const large = (endDeg - startDeg) > 180 ? 1 : 0;
  return `M${x1.toFixed(2)},${y1.toFixed(2)} A${r2},${r2} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} L${x3.toFixed(2)},${y3.toFixed(2)} A${r1},${r1} 0 ${large},0 ${x4.toFixed(2)},${y4.toFixed(2)} Z`;
}

const RINGS_CFG = [
  { key: 'cycle',  innerPct: 0.28, outerPct: 0.38 },
  { key: 'mental', innerPct: 0.42, outerPct: 0.52 },
  { key: 'sport',  innerPct: 0.56, outerPct: 0.66 },
];

const StaticConcentricPie = ({ cycleSlices, mentalSlices, sportSlices, cycleDayPct, cycleDay = null, cycleDays = 28, size = 110 }) => {
  const cx = size / 2, cy = size / 2, R = size / 2;

  const rings = [
    { ...RINGS_CFG[0], slices: toSlices(cycleSlices) },
    { ...RINGS_CFG[1], slices: toSlices(mentalSlices) },
    { ...RINGS_CFG[2], slices: toSlices(sportSlices) },
  ];

  // Le jour vient de l'API quand elle le fournit : l'aiguille plafonne à un tour,
  // le jour non (règles en retard).
  const displayDay = cycleDayPct == null
    ? null
    : cycleDay ?? Math.max(1, Math.min(cycleDays, Math.ceil(cycleDayPct * cycleDays) || 1));

  const phaseColor = useMemo(() => {
    if (cycleDayPct == null || !cycleSlices?.length) return null;
    const total = cycleSlices.reduce((s, d) => s + d.pct, 0);
    if (!total) return null;
    let acc = 0;
    for (const s of cycleSlices) {
      acc += s.pct / total;
      if (cycleDayPct <= acc) return s.color;
    }
    return cycleSlices.at(-1).color;
  }, [cycleDayPct, cycleSlices]);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ display: 'block', aspectRatio: '1 / 1' }}>
      {rings.map(({ slices, innerPct, outerPct, key }) => {
        const total = slices.reduce((s, d) => s + (d.value || 0), 0);
        if (!total) return null;
        let angle = 0;
        return slices.map((slice, i) => {
          const sweep = (slice.value / total) * 360;
          const start = angle;
          angle += sweep;
          if (sweep < 0.1) return null;
          return (
            <path
              key={`${key}-${i}`}
              d={arcPath(cx, cy, R * innerPct, R * outerPct, start, start + sweep)}
              fill={slice.fill}
            />
          );
        });
      })}

      {displayDay != null && (
        <text
          x={cx} y={cy}
          fontSize={size * 0.165}
          fontWeight="800"
          fill={phaseColor ?? UI.textPrimary}
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="system-ui, sans-serif"
          style={{ pointerEvents: 'none' }}
        >
          J{displayDay}
        </text>
      )}

      {cycleDayPct != null && (() => {
        const rad = ((cycleDayPct * 360 - 90) * Math.PI) / 180;
        const x1 = (cx + R * 0.22 * Math.cos(rad)).toFixed(2);
        const y1 = (cy + R * 0.22 * Math.sin(rad)).toFixed(2);
        const x2 = (cx + R * 0.72 * Math.cos(rad)).toFixed(2);
        const y2 = (cy + R * 0.72 * Math.sin(rad)).toFixed(2);
        return (
          <g style={{ pointerEvents: 'none' }}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={UI.surfaceEmpty} strokeWidth="3.5" strokeLinecap="round" />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={UI.white} strokeWidth="2" strokeLinecap="round" />
          </g>
        );
      })()}
    </svg>
  );
};

export default StaticConcentricPie;
